import { useState, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Mail, Refresh, CheckCircle } from "@mui/icons-material";

import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { AuthLayout, SignInWrapper } from "./auth.style";
import { StyledButton } from "../Common/styles/button";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { DarkLogo } from "../Common/assets/Sidebar";

const schema = yup.object({
  confirmationCode: yup
    .string()
    .required("*Confirmation code is required")
    .length(6, "*Code must be exactly 6 digits")
    .matches(/^\d+$/, "*Code must contain only numbers"),
});

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = searchParams.get("token");

  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendCode = async () => {
    setIsResending(true);
    setError("");
    try {
      await http.post("/participants/resend-confirmation-code", { email });
      setResendCooldown(60); // 60 second cooldown
      toastMessage("success", "Confirmation code sent successfully!");
    } catch (error) {
      setError("Failed to resend code. Please try again.");
      errorToastMessage(error as Error);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyCode = async (data: any) => {
    setIsVerifying(true);
    setError("");

    try {
      const body = {
        token: token,
        code: data.confirmationCode.trim(),
      };
      await http.post("/participants/verify-email", body);
      setIsVerified(true);
      toastMessage("success", "Email verified successfully!");
      setTimeout(() => {
        navigate("/registry/login");
      }, 2000);
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          "Invalid confirmation code. Please try again."
      );
      errorToastMessage(error as Error);
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerified) {
    return (
      <Box sx={AuthLayout}>
        <Box sx={SignInWrapper}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
            <DarkLogo />
          </Box>

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                boxShadow: "0 4px 16px rgba(34, 197, 94, 0.2)",
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: "white" }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1e3a8a",
                mb: 1,
              }}
            >
              Email Verified!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                mb: 2,
              }}
            >
              Your email has been successfully verified.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
              }}
            >
              Redirecting you to Login...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={AuthLayout}>
      <Box sx={SignInWrapper}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          <DarkLogo />
        </Box>

        {/* Verification Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Enter confirmation code
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#64748b",
            }}
          >
            We've sent a 6-digit confirmation code to{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {email}
            </Typography>
            . Please enter the code below to verify your account.
          </Typography>
        </Box>

        <Formik
          initialValues={{
            confirmationCode: "",
          }}
          onSubmit={handleVerifyCode}
          validationSchema={schema}
        >
          {({ errors, touched, getFieldProps }) => {
            return (
              <Form>
                {/* Confirmation Code Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="confirmationCode">
                    Confirmation Code
                  </FormLabel>
                  <TextField
                    placeholder="Enter 6-digit code"
                    id="confirmationCode"
                    {...getFieldProps("confirmationCode")}
                    error={
                      touched?.confirmationCode && errors?.confirmationCode
                        ? true
                        : false
                    }
                    helperText={
                      touched?.confirmationCode && errors?.confirmationCode
                        ? errors?.confirmationCode
                        : " "
                    }
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: "center",
                        fontSize: "18px",
                        letterSpacing: "2px",
                      },
                    }}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      getFieldProps("confirmationCode").onChange({
                        target: { name: "confirmationCode", value },
                      });
                      setError("");
                    }}
                  />
                </FormControl>

                {/* Error Alert */}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Verify Button */}
                {!isVerifying ? (
                  <StyledButton variant="contained" type="submit" fullWidth>
                    Verify Email
                  </StyledButton>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={25} />
                  </Box>
                )}

                {/* Resend Code Section */}
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                    Didn't receive the code? Check your spam folder or request a
                    new one.
                  </Typography>

                  <StyledButton
                    variant="outlined"
                    onClick={handleResendCode}
                    disabled={isResending || resendCooldown > 0}
                    startIcon={
                      isResending ? (
                        <Refresh
                          sx={{ animation: "spin 1s linear infinite" }}
                        />
                      ) : (
                        <Mail />
                      )
                    }
                    sx={{ width: "100%" }}
                  >
                    {isResending
                      ? "Sending..."
                      : resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend Code"}
                  </StyledButton>
                </Box>

                {/* Support Contact */}
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Having trouble? Contact us at{" "}
                    <NavLink
                      to="mailto:support@crohnscolitisfoundation.org"
                      style={{ color: "#1d4ed8", textDecoration: "none" }}
                    >
                      support@crohnscolitisfoundation.org
                    </NavLink>
                  </Typography>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
};

export default EmailVerification;
